import { TelemetryEvent } from 'smash-telemetry';

/**
 * Generic message shipped in place of any server-derived error text. The real message --
 * Angular's own `HttpErrorResponse#message` embeds the request URL, and the response body in
 * `HttpErrorResponse#error` can carry arbitrary server output -- must never reach
 * `smash-telemetry`'s ingestion endpoint, which is unauthenticated.
 */
const REDACTED_HTTP_ERROR_MESSAGE = 'An HTTP request failed.';

/** Header names (lower-cased) recognized as carrying a request correlation id. */
const CORRELATION_ID_HEADER_NAMES = ['x-correlation-id', 'x-request-id', 'x-amzn-requestid'];

interface ProjectedHttpErrorResponse {
    name?: unknown;
    status?: unknown;
    headers?: unknown;
}

/**
 * D3: `GlobalErrorHandler.handleError()` also routes every error through `LoggerService.error()`,
 * which does `console.error(\`Error: ${message}\`, stack)` -- and for an `HttpErrorResponse`,
 * `message` is Angular's own generic diagnostic string (built verbatim in
 * `@angular/common/fesm2022/http.mjs`'s `HttpErrorResponse` constructor). `smash-telemetry`'s
 * console capture turns that call into its own event (`CaptureSource.Console`), joining every
 * string argument as-is into `data.message` -- never JSON, so it never reaches the
 * `isProjectedHttpErrorResponse` path above. This pattern recognizes that leaked diagnostic string
 * wherever it appears in a captured console message, regardless of which call site logged it or
 * what surrounds it.
 *
 * Angular's constructor produces the message in one of TWO shapes, chosen by `status`:
 *   - `status` in [200,300) (a "successful" status whose BODY still failed to parse per the
 *     requested `responseType`): `` `Http failure during parsing for ${url}` `` -- no
 *     status/statusText suffix at all.
 *   - otherwise: `` `Http failure response for ${url}: ${status} ${statusText}` ``.
 * In both shapes `url` itself falls back to the literal `(unknown url)` (note the internal space)
 * whenever the request URL is falsy -- so a pattern anchored on "no whitespace in the URL token"
 * (`\S+`) misses that fallback on the "response for" shape, and a pattern that only recognizes the
 * "response for" shape entirely misses the "parsing" shape.
 *
 * Rather than special-casing each shape as its own literal alternative, both alternatives below
 * share the stable `Http failure ` prefix and tolerate an arbitrary (whitespace-including) URL
 * token via a greedy `.*`/`.+` (which does not cross the newline that separates the message from
 * an appended stack trace, so it cannot over-match into unrelated text). For the "response for"
 * shape, the greedy match backtracks to the LAST `: <digits> ` occurrence in the line, which is
 * where the real status lives even if the URL itself happens to contain a colon (e.g. a
 * `host:port`). The "during parsing" shape carries no capture group, so its status is correctly
 * left unrecoverable rather than defaulted to a guess.
 *
 * The `statusText` token after the status digits is matched with `\S*` (zero or more), not `\S+`:
 * an HTTP/2 response (S3 and CloudFront both serve over HTTP/2) carries no textual reason phrase,
 * so Angular's `HttpErrorResponse#statusText` is the empty string and the message it builds ends
 * in `${status} ` -- a trailing space with either nothing after it (end of string) or a stack
 * trace's leading newline immediately after. `\S+` demands at least one non-whitespace character
 * there and fails to match either case, which used to leave the credential-bearing URL completely
 * unredacted; `\S*` accepts the empty `statusText` while still matching a populated one exactly as
 * before.
 */
const CONSOLE_HTTP_ERROR_MESSAGE_PATTERN = /Http failure response for .*: (\d+) \S*|Http failure during parsing for .+/;

function tryParseJson(value: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        return undefined;
    }
}

/**
 * `HttpErrorResponse` does not extend `Error` (it only implements the `Error` interface), so
 * `smash-telemetry`'s own `captureError` cannot take the cheap `error.message` path for it and
 * instead JSON-serializes the *entire* object -- url (with its query string), response body,
 * headers, status -- into `data.message`. `name` is always set to the literal
 * `"HttpErrorResponse"` by Angular's constructor, which makes it a reliable, narrow discriminator
 * for "this captured event is an HttpErrorResponse projection" without guessing from the rest of
 * the shape.
 */
function isProjectedHttpErrorResponse(value: unknown): value is ProjectedHttpErrorResponse {
    return isRecord(value) && value['name'] === 'HttpErrorResponse';
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

/**
 * Best-effort extraction of a correlation id out of the JSON-safe projection of an Angular
 * `HttpHeaders` instance. Returns `undefined` -- never a guess -- when no known correlation-id
 * header is present: `HttpHeaders` lazily initializes its internal map, so it is frequently empty
 * at capture time, and this function must stay conservative rather than fabricate an id.
 *
 * @param projectedHeaders The projected `headers` field, of unknown shape at this boundary.
 * @returns The correlation id when found, `undefined` otherwise.
 */
function extractCorrelationId(projectedHeaders: unknown): string | undefined {
    const candidates = [projectedHeaders, isRecord(projectedHeaders) ? projectedHeaders['headers'] : undefined];
    for (const candidate of candidates) {
        if (!isRecord(candidate)) {
            continue;
        }
        for (const [headerName, headerValue] of Object.entries(candidate)) {
            if (!CORRELATION_ID_HEADER_NAMES.includes(headerName.toLowerCase())) {
                continue;
            }
            const singleValue = Array.isArray(headerValue) ? headerValue[0] : headerValue;
            if (typeof singleValue === 'string' && singleValue.length > 0) {
                return singleValue;
            }
        }
    }
    return undefined;
}

/**
 * `smash-telemetry` `beforeSend` hook -- the library's own "last chance to scrub or drop an event
 * before it is queued" (see `TelemetryOptions.beforeSend`). Wired in `bootstrapTelemetry.ts`, so
 * it runs for every event captured through any {@link import('smash-telemetry').CaptureSource},
 * not only `GlobalErrorHandler`.
 *
 * When the event is an `HttpErrorResponse` projection, everything that could carry request or
 * response detail (the URL and its query string, the response body, headers, `statusText`) is
 * dropped; only the HTTP status code, a fixed generic message, and -- when one can be found -- a
 * correlation id survive. The same redaction applies when an `HttpErrorResponse` instead leaked in
 * through a plain `console.error` string (see {@link CONSOLE_HTTP_ERROR_MESSAGE_PATTERN}) -- only
 * the status code survives there, and only when the message shape actually carries one (Angular's
 * "parsing failure" shape does not, so `status` is omitted rather than defaulted to a guess); no
 * structured headers are available there either, so no correlation id is ever recovered from free
 * text. Every other event (a plain `Error`, an unrelated `console.*` call, a Web Vital) is returned
 * unchanged.
 *
 * @param event Event that cleared sampling, about to be queued for delivery.
 * @returns The event unchanged, or a redacted copy when it carries an `HttpErrorResponse`.
 */
export function scrubHttpErrorForTelemetry(event: TelemetryEvent): TelemetryEvent {
    const message = event.data['message'];
    if (typeof message !== 'string') {
        return event;
    }
    const parsed = tryParseJson(message);
    if (isProjectedHttpErrorResponse(parsed)) {
        const correlationId = extractCorrelationId(parsed.headers);
        return {
            ...event,
            data: {
                ...event.data,
                message: REDACTED_HTTP_ERROR_MESSAGE,
                status: typeof parsed.status === 'number' ? parsed.status : undefined,
                correlationId,
            },
        };
    }
    const consoleMatch = message.match(CONSOLE_HTTP_ERROR_MESSAGE_PATTERN);
    if (consoleMatch) {
        const statusDigits = consoleMatch[1];
        return {
            ...event,
            data: {
                ...event.data,
                message: REDACTED_HTTP_ERROR_MESSAGE,
                status: typeof statusDigits === 'string' ? Number(statusDigits) : undefined,
            },
        };
    }
    return event;
}
